import React, {
  useState, useEffect, useCallback, useMemo,
} from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, Plus, Trash2, CheckCircle2, XCircle, Info,
} from 'lucide-react';

import TextInput from '../../../components/ui/TextInput.jsx';
import Button from '../../../components/ui/Button';
import CheckboxInput from '../../../components/ui/CheckboxInput';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import { formatDateTime } from '../../../utils/Utils';
import { Badge, ONE_ON_ONE_STATUS_COLORS, ONE_ON_ONE_ROLE_COLORS } from '../config';

const Container = styled.div``;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  margin-bottom: 10px;
  color: ${({ theme }) => theme.colors.primary.main};
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 600;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const PersonName = styled.h2`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const DateLabel = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #64748B;
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const Banner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #EFF6FF;
  border: 1px solid #DBEAFE;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 16px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 10px;
  padding: 16px 18px;
  margin-bottom: 16px;
`;

const CardTitle = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 12px;
`;

const HintText = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #64748B;
  margin-bottom: 10px;
`;

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid #F1F5F9;
  &:last-child { border-bottom: none; }
`;

const ItemText = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  text-decoration: ${(p) => (p.$done ? 'line-through' : 'none')};
  opacity: ${(p) => (p.$done ? 0.6 : 1)};
`;

const DeleteBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  padding: 4px;
  color: #A0A8B4;
  &:hover { color: #E53935; }
`;

const AddRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;
  align-items: flex-end;
`;

const AddInputWrap = styled.div`
  flex: 1;
`;

const EmptyHint = styled.div`
  padding: 12px 0;
  color: #A0A8B4;
  font-size: 13px;
`;

const SaveRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
`;

// Gündem (talking_point) / aksiyon (action_item) bölümü — ekle/işaretle/sil.
// Metin DÜZENLEME YOK (task-4-brief.md yalnız "ekle (metin), done toggle,
// sil" istiyor; UpdateOneOnOneItemRequest.text alanı BE'de var ama bilinçli
// olarak kullanılmadı — kapsam dışı, task-4-report.md'de not edildi).
function ItemSection({
  title, itemType, items, addPlaceholder, emptyLabel, onAdd, onToggle, onDeleteRequest,
}) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [adding, setAdding] = useState(false);

  const sorted = useMemo(
    () => items
      .filter((it) => it.itemType === itemType)
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder),
    [items, itemType],
  );

  const handleAdd = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setAdding(true);
    const ok = await onAdd({ itemType, text: trimmed });
    if (ok) setText('');
    setAdding(false);
  }, [text, itemType, onAdd]);

  return (
    <Card>
      <CardTitle>{title}</CardTitle>
      {sorted.length === 0 ? (
        <EmptyHint>{emptyLabel}</EmptyHint>
      ) : (
        sorted.map((item) => (
          <ItemRow key={item.id} data-role={`item-row-${item.id}`}>
            <CheckboxInput
              label={<ItemText $done={item.isDone}>{item.text}</ItemText>}
              checked={item.isDone}
              onChange={() => onToggle(item)}
            />
            <DeleteBtn type="button" onClick={() => onDeleteRequest(item)} aria-label={t('common.delete')}>
              <Trash2 size={14} />
            </DeleteBtn>
          </ItemRow>
        ))
      )}
      <AddRow>
        <AddInputWrap>
          <TextInput
            data-role={`add-input-${itemType}`}
            placeholder={addPlaceholder}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </AddInputWrap>
        <Button
          data-role={`add-btn-${itemType}`}
          variant="outline"
          onClick={handleAdd}
          disabled={!text.trim() || adding}
        >
          <Plus size={14} />
          {t('oneOnOnes.detail.addItem')}
        </Button>
      </AddRow>
    </Card>
  );
}

// Faz D (spec 2026-08-08 §4, Task 4) — 1:1 detay görünümü. `meeting` her
// zaman OneOnOnesPage'in `data` dizisinden (id ile) gelir — GET /{id} ucu
// YOK, ayrı bir fetch YOK (task-1-report.md §4). `myPrivateNote` DTO'da
// YALNIZ çağıranın kendi notu — karşı tarafınki hiç gelmez, bu yüzden FE
// hiçbir koşul üretmez, geleni olduğu gibi render eder (task-1-report.md
// dosya başı yorumu, aynı ilke burada da geçerli).
export default function OneOnOneDetailView({
  meeting, carryOverCount, onBack, onUpdateMeeting, onAddItem, onUpdateItem, onDeleteItem,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [sharedNotesDraft, setSharedNotesDraft] = useState('');
  const [privateNoteDraft, setPrivateNoteDraft] = useState('');
  const [savingShared, setSavingShared] = useState(false);
  const [savingPrivate, setSavingPrivate] = useState(false);
  const [completeConfirm, setCompleteConfirm] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, item: null });
  const [deleting, setDeleting] = useState(false);

  // Taslaklar yalnız MEETING DEĞİŞTİĞİNDE (farklı bir görüşme seçildiğinde)
  // sıfırlanır — başarılı bir kaydetmeden sonra aynı meeting.id için gelen
  // taze prop, kullanıcının o an düzenlemekte olduğu taslağın üzerine
  // YAZILMAZ (zaten kaydedilen değerle taslak eşit olur).
  useEffect(() => {
    setSharedNotesDraft(meeting?.sharedNotes || '');
    setPrivateNoteDraft(meeting?.myPrivateNote || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting?.id]);

  const handleSaveShared = useCallback(async () => {
    setSavingShared(true);
    await onUpdateMeeting(meeting.id, { sharedNotes: sharedNotesDraft });
    setSavingShared(false);
  }, [onUpdateMeeting, meeting, sharedNotesDraft]);

  const handleSavePrivate = useCallback(async () => {
    setSavingPrivate(true);
    await onUpdateMeeting(meeting.id, { myPrivateNote: privateNoteDraft });
    setSavingPrivate(false);
  }, [onUpdateMeeting, meeting, privateNoteDraft]);

  const handleComplete = useCallback(async () => {
    setStatusLoading(true);
    await onUpdateMeeting(meeting.id, { status: 'completed' });
    setStatusLoading(false);
    setCompleteConfirm(false);
  }, [onUpdateMeeting, meeting]);

  const handleCancelMeeting = useCallback(async () => {
    setStatusLoading(true);
    await onUpdateMeeting(meeting.id, { status: 'cancelled' });
    setStatusLoading(false);
    setCancelConfirm(false);
  }, [onUpdateMeeting, meeting]);

  const handleAddItem = useCallback(
    (payload) => onAddItem(meeting.id, payload).then((it) => !!it),
    [onAddItem, meeting],
  );

  const handleToggle = useCallback((item) => {
    onUpdateItem(meeting.id, item.id, { isDone: !item.isDone });
  }, [onUpdateItem, meeting]);

  const requestDelete = useCallback((item) => setDeleteConfirm({ isOpen: true, item }), []);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    await onDeleteItem(meeting.id, deleteConfirm.item.id);
    setDeleteConfirm({ isOpen: false, item: null });
    setDeleting(false);
  }, [onDeleteItem, meeting, deleteConfirm]);

  if (!meeting) {
    return (
      <Container>
        <BackButton type="button" onClick={onBack}><ArrowLeft size={14} />{t('oneOnOnes.backToList')}</BackButton>
        <EmptyHint>{t('oneOnOnes.detail.notFound')}</EmptyHint>
      </Container>
    );
  }

  const counterpartName = meeting.myRole === 'manager' ? meeting.employeeName : meeting.managerName;
  const statusCfg = ONE_ON_ONE_STATUS_COLORS[meeting.status] || ONE_ON_ONE_STATUS_COLORS.scheduled;
  const roleCfg = ONE_ON_ONE_ROLE_COLORS[meeting.myRole] || ONE_ON_ONE_ROLE_COLORS.employee;

  return (
    <Container>
      <ConfirmDialog
        isOpen={completeConfirm}
        title={t('oneOnOnes.completeConfirm.title')}
        message={t('oneOnOnes.completeConfirm.message')}
        onConfirm={handleComplete}
        onCancel={() => setCompleteConfirm(false)}
        loading={statusLoading}
        type="info"
      />
      <ConfirmDialog
        isOpen={cancelConfirm}
        title={t('oneOnOnes.cancelConfirm.title')}
        message={t('oneOnOnes.cancelConfirm.message')}
        onConfirm={handleCancelMeeting}
        onCancel={() => setCancelConfirm(false)}
        loading={statusLoading}
        type="danger"
      />
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('oneOnOnes.deleteItemConfirm.title')}
        message={t('oneOnOnes.deleteItemConfirm.message')}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, item: null })}
        loading={deleting}
        type="danger"
      />

      <BackButton type="button" onClick={onBack}><ArrowLeft size={14} />{t('oneOnOnes.backToList')}</BackButton>

      <HeaderRow>
        <TitleGroup>
          <PersonName>{counterpartName}</PersonName>
          <Badge color={roleCfg.color} bg={roleCfg.bg}>
            {t(`oneOnOnes.role.${meeting.myRole}`, { defaultValue: meeting.myRole })}
          </Badge>
          <Badge color={statusCfg.color} bg={statusCfg.bg}>
            {t(`oneOnOnes.status.${meeting.status}`, { defaultValue: meeting.status })}
          </Badge>
          <DateLabel>{formatDateTime(meeting.scheduledAt, lang)}</DateLabel>
        </TitleGroup>
        {meeting.status === 'scheduled' && (
          <ActionsRow>
            <Button onClick={() => setCompleteConfirm(true)}>
              <CheckCircle2 size={14} />
              {t('oneOnOnes.actions.complete')}
            </Button>
            <Button variant="outline" onClick={() => setCancelConfirm(true)}>
              <XCircle size={14} />
              {t('oneOnOnes.actions.cancel')}
            </Button>
          </ActionsRow>
        )}
      </HeaderRow>

      {carryOverCount > 0 && (
        <Banner data-role="carry-over-banner">
          <Info size={14} />
          {t('oneOnOnes.detail.carryOverBanner', { count: carryOverCount, defaultValue: `${carryOverCount}` })}
        </Banner>
      )}

      <ItemSection
        title={t('oneOnOnes.detail.agendaTitle')}
        itemType="talking_point"
        items={meeting.items}
        addPlaceholder={t('oneOnOnes.detail.addAgendaPlaceholder')}
        emptyLabel={t('oneOnOnes.detail.noAgendaItems')}
        onAdd={handleAddItem}
        onToggle={handleToggle}
        onDeleteRequest={requestDelete}
      />

      <ItemSection
        title={t('oneOnOnes.detail.actionsTitle')}
        itemType="action_item"
        items={meeting.items}
        addPlaceholder={t('oneOnOnes.detail.addActionPlaceholder')}
        emptyLabel={t('oneOnOnes.detail.noActionItems')}
        onAdd={handleAddItem}
        onToggle={handleToggle}
        onDeleteRequest={requestDelete}
      />

      <Card>
        <CardTitle>{t('oneOnOnes.detail.sharedNotesTitle')}</CardTitle>
        <TextInput
          data-role="shared-notes-input"
          type="textarea"
          rows={4}
          value={sharedNotesDraft}
          onChange={(e) => setSharedNotesDraft(e.target.value)}
        />
        <SaveRow>
          <Button
            data-role="shared-notes-save-btn"
            onClick={handleSaveShared}
            disabled={savingShared || sharedNotesDraft === (meeting.sharedNotes || '')}
          >
            {savingShared ? t('common.saving') : t('common.save')}
          </Button>
        </SaveRow>
      </Card>

      <Card>
        <CardTitle>{t('oneOnOnes.detail.privateNoteTitle')}</CardTitle>
        <HintText>{t('oneOnOnes.detail.privateNoteHint')}</HintText>
        <TextInput
          data-role="private-note-input"
          type="textarea"
          rows={4}
          value={privateNoteDraft}
          onChange={(e) => setPrivateNoteDraft(e.target.value)}
        />
        <SaveRow>
          <Button
            data-role="private-note-save-btn"
            onClick={handleSavePrivate}
            disabled={savingPrivate || privateNoteDraft === (meeting.myPrivateNote || '')}
          >
            {savingPrivate ? t('common.saving') : t('common.save')}
          </Button>
        </SaveRow>
      </Card>
    </Container>
  );
}
